/**
 * Copy of raas-widgets-backend/src/common/validation/bank-account-number.ts
 * (inspect + prefix helpers only). Keep in sync with the backend.
 */
export const BANK_ACCOUNT_NUMBER_MAX_LENGTH = 34;

export const BANK_ACCOUNT_NUMBER_PATTERN = /^[A-Z0-9]+$/;

/**
 * Normalizes electronic bank-account identifiers (including IBAN) before
 * validation. Spaces are presentation-only in formatted IBANs.
 */
export const normalizeBankAccountNumber = (value: unknown): unknown =>
  typeof value === 'string' ? value.replace(/\s/g, '').toUpperCase() : value;

export type BankAccountFormatKind =
  | 'clabe'
  | 'cbu'
  | 'cci'
  | 'iban'
  | 'generic';

export interface BankAccountInspection {
  normalized: string;
  format: BankAccountFormatKind;
  /** False when length or the country check digit fails. */
  formatOk: boolean;
  /** Bank code carried inside the number, when the format has one. */
  bankCode: string | null;
  /**
   * False only when the length is wrong for the destination country.
   * A bad check digit keeps this true. Bank mismatch never blocks.
   */
  canContinue: boolean;
}

/** Destinations that use IBAN as the primary account identifier. */
const IBAN_PRIMARY_COUNTRIES = new Set([
  'AD',
  'AT',
  'BE',
  'BG',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'EU',
  'FI',
  'FR',
  'GB',
  'GI',
  'GR',
  'HR',
  'HU',
  'IE',
  'IS',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MC',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
  'SM',
]);

const IBAN_LENGTH: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
};

const IBAN_BANK_SLICE: Record<string, { start: number; end: number }> = {
  ES: { start: 4, end: 8 },
  DE: { start: 4, end: 12 },
  FR: { start: 4, end: 9 },
};

const CLABE_WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
const CBU_BLOCK1_WEIGHTS = [7, 1, 3, 9, 7, 1, 3];
const CBU_BLOCK2_WEIGHTS = [3, 9, 7, 1, 3, 9, 7, 1, 3, 9, 7, 1, 3];

const asNormalizedString = (value: unknown): string => {
  const normalized = normalizeBankAccountNumber(value);
  return typeof normalized === 'string' ? normalized : '';
};

const mod97 = (numeric: string): number => {
  let remainder = 0;
  for (const ch of numeric) {
    remainder = (remainder * 10 + (ch.charCodeAt(0) - 48)) % 97;
  }
  return remainder;
};

const weightedCheckDigit = (digits: string, weights: number[]): number => {
  let sum = 0;
  for (let i = 0; i < weights.length; i += 1) {
    sum += Number(digits[i]) * weights[i];
  }
  return (10 - (sum % 10)) % 10;
};

const isValidClabe = (digits: string): boolean => {
  if (!/^\d{18}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 17; i += 1) {
    sum += (Number(digits[i]) * CLABE_WEIGHTS[i]) % 10;
  }
  return (10 - (sum % 10)) % 10 === Number(digits[17]);
};

const isValidCbu = (digits: string): boolean => {
  if (!/^\d{22}$/.test(digits)) return false;
  const check1 = weightedCheckDigit(digits.slice(0, 7), CBU_BLOCK1_WEIGHTS);
  const check2 = weightedCheckDigit(digits.slice(8, 21), CBU_BLOCK2_WEIGHTS);
  return check1 === Number(digits[7]) && check2 === Number(digits[21]);
};

/** CCI uses ISO 7064 Mod 97-10 on the 20-digit identifier. */
const isValidCci = (digits: string): boolean =>
  /^\d{20}$/.test(digits) && mod97(digits) === 1;

const looksLikeIban = (value: string): boolean =>
  /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value);

const isValidIban = (value: string): boolean => {
  if (!looksLikeIban(value)) return false;
  if (value.length < 15 || value.length > BANK_ACCOUNT_NUMBER_MAX_LENGTH) {
    return false;
  }
  const expected = IBAN_LENGTH[value.slice(0, 2)];
  if (expected != null && value.length !== expected) return false;
  const rearranged = value.slice(4) + value.slice(0, 4);
  let numeric = '';
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    numeric += code >= 65 ? String(code - 55) : ch;
  }
  return mod97(numeric) === 1;
};

const ibanCountryMatches = (iban: string, country: string): boolean => {
  if (country === 'EU') return true;
  return iban.slice(0, 2) === country;
};

const extractIbanBankCode = (iban: string): string | null => {
  const slice = IBAN_BANK_SLICE[iban.slice(0, 2)];
  if (!slice || iban.length < slice.end) return null;
  return iban.slice(slice.start, slice.end);
};

const fail = (
  normalized: string,
  format: BankAccountFormatKind,
  bankCode: string | null = null,
): BankAccountInspection => ({
  normalized,
  format,
  formatOk: false,
  bankCode,
  canContinue: false,
});

const pass = (
  normalized: string,
  format: BankAccountFormatKind,
  bankCode: string | null,
): BankAccountInspection => ({
  normalized,
  format,
  formatOk: true,
  bankCode,
  canContinue: true,
});

/** Length is acceptable, but the check digit or IBAN country is not. */
const warn = (
  normalized: string,
  format: BankAccountFormatKind,
  bankCode: string | null = null,
): BankAccountInspection => ({
  normalized,
  format,
  formatOk: false,
  bankCode,
  canContinue: true,
});

/**
 * Inspects a bank-account identifier for the destination country.
 * Wrong length blocks storage. A bad check digit only warns.
 */
export const inspectBankAccountNumber = (
  country: string | null | undefined,
  raw: unknown,
): BankAccountInspection => {
  const normalized = asNormalizedString(raw);
  if (
    !normalized ||
    normalized.length > BANK_ACCOUNT_NUMBER_MAX_LENGTH ||
    !BANK_ACCOUNT_NUMBER_PATTERN.test(normalized)
  ) {
    return fail(normalized, 'generic');
  }

  const dest = country?.trim().toUpperCase() || '';

  if (dest === 'MX') {
    const bankCode = /^\d{3}/.test(normalized) ? normalized.slice(0, 3) : null;
    if (!/^\d{18}$/.test(normalized)) return fail(normalized, 'clabe', bankCode);
    return isValidClabe(normalized)
      ? pass(normalized, 'clabe', bankCode)
      : warn(normalized, 'clabe', bankCode);
  }

  if (dest === 'AR') {
    const bankCode = /^\d{3}/.test(normalized) ? normalized.slice(0, 3) : null;
    if (!/^\d{22}$/.test(normalized)) return fail(normalized, 'cbu', bankCode);
    return isValidCbu(normalized)
      ? pass(normalized, 'cbu', bankCode)
      : warn(normalized, 'cbu', bankCode);
  }

  if (dest === 'PE') {
    const bankCode = /^\d{3}/.test(normalized) ? normalized.slice(0, 3) : null;
    if (!/^\d{20}$/.test(normalized)) return fail(normalized, 'cci', bankCode);
    return isValidCci(normalized)
      ? pass(normalized, 'cci', bankCode)
      : warn(normalized, 'cci', bankCode);
  }

  if (IBAN_PRIMARY_COUNTRIES.has(dest)) {
    const bankCode = extractIbanBankCode(normalized);
    const expected = dest === 'EU' ? undefined : IBAN_LENGTH[dest];
    if (expected != null && normalized.length !== expected) {
      return fail(normalized, 'iban', bankCode);
    }
    const ok = isValidIban(normalized) && ibanCountryMatches(normalized, dest);
    return ok
      ? pass(normalized, 'iban', bankCode)
      : warn(normalized, 'iban', bankCode);
  }

  if (looksLikeIban(normalized)) {
    const bankCode = extractIbanBankCode(normalized);
    const expected = IBAN_LENGTH[normalized.slice(0, 2)];
    if (expected != null && normalized.length !== expected) {
      return fail(normalized, 'iban', bankCode);
    }
    const ok =
      isValidIban(normalized) && (!dest || ibanCountryMatches(normalized, dest));
    return ok
      ? pass(normalized, 'iban', bankCode)
      : warn(normalized, 'iban', bankCode);
  }

  return pass(normalized, 'generic', null);
};

/**
 * True when the catalog has prefixes and the extracted bank code is not one of them.
 */
export const bankPrefixMismatch = (
  bankCode: string | null | undefined,
  prefixes: string[] | null | undefined,
): boolean => {
  if (!bankCode) return false;
  if (!prefixes || prefixes.length === 0) return false;
  const wanted = bankCode.trim().toUpperCase();
  return !prefixes.some((prefix) => prefix.trim().toUpperCase() === wanted);
};
